import { useMemo, useState } from "react";
import { ArrowUturnLeftIcon, CheckIcon, PlusIcon, SparklesIcon, TrashIcon } from "@heroicons/react/24/outline";
import { toast } from "../../utils/toast";
import { Button, EmptyState, InfoBox, Input, RowAction, Textarea } from "../ui";
import { useDraft } from "../../hooks/useDraft";
import { errorMessageOf, problemOf, problemMessage, scholarshipsApi } from "../../api/scholarships";
import { kindOf, useScholarshipChange } from "./model";
import { canManageScholarships } from "./access";
import ReasonField, { reasonErrorOf, withReason } from "./ReasonField";
import ProgrammePicker, { useOfferedProgramme } from "./ProgrammePicker";

const NO_CHANGES = {};
const MAX_CRITERIA = 20;

// HEC's assessment criteria (1 November 2026), to start from; a research proposal is marked for Master's and PhD programmes only
const RESEARCH_LEVELS = /master|phd|doctor/i;
const COURSE = { name: "Course", guideline: "Relevance of the course to Rwanda's priority sectors" };
const LEVEL = { name: "Level of study", guideline: "Fit between the student's studies so far and the level applied for" };
const GRADES = { name: "Grades", guideline: "Final grade and consistency across the transcript" };
const RECOMMENDATION = { name: "Recommendation", guideline: "Strength of the recommendation letters" };
const MOTIVATION = { name: "Motivation letter", guideline: "Clarity of goals and commitment to return" };
const PROPOSAL = { name: "Research proposal", guideline: "Quality, feasibility and relevance of the proposed research" };

export const standardRubricFor = (levelName) =>
  RESEARCH_LEVELS.test(levelName ?? "")
    ? [
        { ...COURSE, weight: 10 },
        { ...LEVEL, weight: 10 },
        { ...GRADES, weight: 30 },
        { ...RECOMMENDATION, weight: 10 },
        { ...MOTIVATION, weight: 20 },
        { ...PROPOSAL, weight: 20 },
      ]
    : [
        { ...COURSE, weight: 15 },
        { ...LEVEL, weight: 10 },
        { ...GRADES, weight: 40 },
        { ...RECOMMENDATION, weight: 15 },
        { ...MOTIVATION, weight: 20 },
      ];

export const criteriaRowsOf = (rubric = []) => rubric.map(({ name, weight, guideline, bands }) => ({ name, weight: String(weight), guideline: guideline ?? "", bands: bands ?? [] }));
const comparable = (rows) => JSON.stringify(rows.map(({ name, weight, guideline }) => [name.trim(), Number(weight), guideline.trim()]));

// The rows as the API takes them
export const criteriaOf = (rows) => rows.map((row) => ({ name: row.name.trim(), weight: Number(row.weight), ...(row.guideline.trim() && { guideline: row.guideline.trim() }), bands: row.bands }));

export const validateCriteria = (rows) => {
  const errors = {};
  const names = new Set();
  rows.forEach((row, i) => {
    const name = row.name.trim().toLowerCase();
    if (!name) errors[`${i}.name`] = "Name is required";
    else if (names.has(name)) errors[`${i}.name`] = "Each criterion needs a different name";
    names.add(name);
    const weight = Number(row.weight);
    if (!(Number.isInteger(weight) && weight >= 1 && weight <= 100)) errors[`${i}.weight`] = "1 to 100";
  });
  return errors;
};

/**
 * How evaluators mark each offered programme's applications (part of journey step 2), saved with
 * `PUT …/programmes/{programmeId}/rubric`: criteria with weights that add up to 100. Each criterion
 * is marked out of 100 and the weighted total is the application's score. A programme's criteria
 * lock once anyone has marked one of its applications.
 */
export default function CriteriaTab({ scholarship }) {
  const { one } = kindOf(scholarship.type);
  const change = useScholarshipChange(scholarship.id);
  const { programmes, programme, choose } = useOfferedProgramme(scholarship);
  const [draft, setDraft, clearDraft] = useDraft(`scholarship-rubric:${scholarship.id}:${programme?.id}`, NO_CHANGES);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const locked = Boolean(programme?.rubricLocked);
  const editable = canManageScholarships() && ["DRAFT", "PUBLISHED"].includes(scholarship.state) && !locked;
  const saved = useMemo(() => criteriaRowsOf(programme?.rubric), [programme]);
  // A programme without criteria starts from the standard ones for its level, to change and save
  const rows = draft.rows ?? (saved.length || !editable || !programme ? saved : criteriaRowsOf(standardRubricFor(programme.level?.name)));
  const reason = draft.reason ?? "";
  const changed = comparable(rows) !== comparable(saved);
  const total = rows.reduce((sum, row) => sum + (Number(row.weight) || 0), 0);

  const setRows = (next) => {
    setErrors({});
    // Without saved criteria every edit is kept, so emptying the list does not bring the standard ones back
    setDraft((prev) => (saved.length && comparable(next) === comparable(saved) ? (prev.reason ? { reason: prev.reason } : NO_CHANGES) : { ...prev, rows: next }));
  };
  const updateRow = (i, patch) => setRows(rows.map((row, j) => (j === i ? { ...row, ...patch } : row)));

  const discard = () => {
    clearDraft();
    setErrors({});
  };

  // Copying replaces this programme's criteria, as their weights must add up to 100 together
  const copyFrom = (source) => {
    setRows(criteriaRowsOf(source.rubric));
    toast.success(`Copied the criteria of ${source.name}`);
  };

  const save = async () => {
    const found = validateCriteria(rows);
    const reasonError = reasonErrorOf(scholarship, reason);
    if (reasonError) found.reason = reasonError;
    if (Object.keys(found).length) return setErrors(found);
    const criteria = criteriaOf(rows);
    setSaving(true);
    try {
      await change((etag) =>
        scholarshipsApi.put(`admin/scholarships/${scholarship.id}/programmes/${programme.id}/rubric`, withReason(scholarship, { criteria }, reason), { ifMatch: etag }),
      );
      toast.success(`Criteria for ${programme.name} saved`);
      discard();
    } catch (error) {
      console.error(`Failed to save the criteria of programme ${programme.id} on scholarship ${scholarship.id}:`, error);
      const problem = problemOf(error);
      if (problem?.errors?.length) setErrors({ form: problem.errors.map((e) => problemMessage(e.code) ?? e.code).join(" ") });
      toast.error(errorMessageOf(error, "Failed to save the criteria. Please try again."));
    } finally {
      setSaving(false);
    }
  };

  const disabled = !editable || saving;

  if (!programmes.length) {
    return <EmptyState title="No programmes yet" description={`Add the programmes this ${one} offers first; each programme has its own marking criteria.`} />;
  }

  return (
    <div className="flex flex-col gap-5">
      <ProgrammePicker
        programmes={programmes}
        programme={programme}
        onChange={(id) => {
          choose(id);
          setErrors({});
        }}
        countOf={(p) => (p.rubric ?? []).length}
        item="criterion"
        items="criteria"
        copyLabel="Copy criteria from"
        onCopy={editable ? copyFrom : null}
        disabled={saving}
      />
      {locked && <InfoBox>Locked: marking has started for this programme.</InfoBox>}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-amber-700">{changed && "Unsaved changes"}</p>
        <span className={`rounded-md px-2 py-1 text-sm font-semibold ${total === 100 ? "bg-green-100 text-green-800" : "bg-amber-100 text-amber-800"}`}>
          Weights: {total} / 100
        </span>
      </div>

      {rows.length === 0 ? (
        <div className="flex flex-col items-center gap-3 rounded-lg border border-dashed border-slate-200 p-8 text-center">
          <p className="text-sm text-slate-600">No criteria yet.</p>
          {editable && (
            <Button variant="outline" icon={<SparklesIcon className="h-4 w-4" />} onClick={() => setRows(criteriaRowsOf(standardRubricFor(programme.level?.name)))}>
              Start from HEC's standard criteria
            </Button>
          )}
        </div>
      ) : (
        <ol className="flex flex-col gap-3">
          {rows.map((row, i) => (
            <li key={i} className="grid gap-3 rounded-lg border border-slate-200 p-4 md:grid-cols-[1fr_8rem_auto]">
              <Input label={`Criterion ${i + 1}`} required maxLength={200} value={row.name} onChange={(e) => updateRow(i, { name: e.target.value })} error={errors[`${i}.name`]} disabled={disabled} />
              <Input type="number" label="Weight" required min={1} max={100} suffix="%" value={row.weight} onChange={(e) => updateRow(i, { weight: e.target.value })} error={errors[`${i}.weight`]} disabled={disabled} />
              <div className="flex items-end">
                {editable && <RowAction Icon={TrashIcon} label="Remove" variant="ghost" onClick={() => setRows(rows.filter((_, j) => j !== i))} disabled={saving} />}
              </div>
              <Textarea className="md:col-span-3" label="Guidance for evaluators" rows={2} maxLength={2000} value={row.guideline} onChange={(e) => updateRow(i, { guideline: e.target.value })} disabled={disabled} />
            </li>
          ))}
        </ol>
      )}

      {editable && rows.length > 0 && rows.length < MAX_CRITERIA && (
        <Button variant="outline" className="self-start" icon={<PlusIcon className="h-4 w-4" />} onClick={() => setRows([...rows, { name: "", weight: "", guideline: "", bands: [] }])} disabled={saving}>
          Add criterion
        </Button>
      )}

      {editable && (
        <div className="flex flex-col gap-4 border-t border-slate-100 pt-5">
          <ReasonField
            scholarship={scholarship}
            value={reason}
            onChange={(value) => {
              setErrors({});
              setDraft((prev) => ({ ...prev, reason: value }));
            }}
            error={errors.reason}
            disabled={saving}
          />
          {errors.form && <p className="text-sm text-red-600">{errors.form}</p>}
          <div className="flex flex-col-reverse justify-end gap-3 sm:flex-row">
            <Button variant="ghost" onClick={discard} disabled={saving || (!changed && !reason)} icon={<ArrowUturnLeftIcon className="h-4 w-4" />}>
              Discard changes
            </Button>
            <Button onClick={save} loading={saving} disabled={!changed} icon={<CheckIcon className="h-4 w-4" />}>
              Save criteria
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
